import { NavigationMenuItem } from '@dfh-corp-sites/ui'
import { cn } from '@dfh-corp-sites/ui/utils'
import React, { useCallback, useMemo } from 'react'

import { MainMenuFragmentFragment } from '../../../graphql/gatsby'
import { setActiveItem, useActiveItem } from '../../../lib/header/state'
import { UseCanonicalLocation } from '../../../lib/pages/hooks/useCanonicalLocation'
import { normalizeSanityLink } from '../../../lib/sanity/link'
import { MainMenuEntry } from './MainMenuEntry'
import { NavDropdown } from './NavDropdown'

interface DesktopMainMenuEntryProps {
  className?: string
  canonicalLocation: UseCanonicalLocation
  entry: NonNullable<NonNullable<MainMenuFragmentFragment['mainMenu']>[number]>
  dropdownRef?: React.RefObject<HTMLDivElement> | null
  threshold?: number
}

export const DesktopMainMenuEntry: React.VFC<DesktopMainMenuEntryProps> = ({
  className,
  canonicalLocation,
  entry,
  dropdownRef,
  threshold,
}) => {
  const activeItem = useActiveItem()
  const isTempActive = useMemo(() => {
    return activeItem === entry.name
  }, [activeItem, entry.name])

  // set isActive if current URL begins with menu entry's path
  const isActive = useMemo(() => {
    if (isTempActive) {
      return true
    }
    const { link } = entry
    const normalized = link ? normalizeSanityLink({ link }) : undefined
    const normalizedHref = normalized?.href
    if (!normalizedHref) {
      return false
    }
    const startsWithRegex = new RegExp(`^${normalizedHref}(?:[/?].*)?$`)

    const isLocationActive = startsWithRegex.test(canonicalLocation.path)
    return isLocationActive
  }, [canonicalLocation.path, entry, isTempActive])

  const setThisActive = useCallback(() => {
    setActiveItem(entry.name)
  }, [entry.name])

  return (
    <NavigationMenuItem
      className={cn(
        'flex flex-row items-center w-fit font-gotham text-2xl md:text-sm lg:text-base font-normal md:font-medium no-underline md:pt-[3px]',
        className,
      )}
      onFocus={setThisActive}
      onMouseEnter={setThisActive}
    >
      <MainMenuEntry menuEntry={entry} isActive={isActive} />
      <NavDropdown
        ref={dropdownRef}
        entry={entry}
        isOpen={isTempActive}
        threshold={threshold ?? 0}
      />
    </NavigationMenuItem>
  )
}
